import React from 'react';
import PropTypes from 'prop-types';
import { Mutation } from 'react-apollo';
import Link from 'next/link';

import { Media } from '@g4/boneyard/lib/Media';

import VerticalSpacing from '@g4/boneyard/lib/VerticalSpacing';

import LinkManager from '../LinkManager';
import { onLogout, LOGOUT_PATH } from '../WithApollo/authUtils';

import { MenuList, MenuItem, MenuLink } from '../TopMenu/Menu';
import FlyoutMenu, { FlyoutLink, FlyoutToggle } from '../TopMenu/FlyoutMenu';
import BurgerMenu from '../TopMenu/BurgerMenu';

import MenuSlide from './MenuSlide';
import logoutMutation from './logoutMutation';
import OffCanvasDrawer from './OffCanvasDrawer';

import myAllegiantMedIcon from '../../images/logo-my-med.svg';
import helpIcon from '../../images/ic-help-dark.svg';

const isLogoutLink = link => link && link.path === LOGOUT_PATH;

const iconMap = {
  MYACCOUNT: myAllegiantMedIcon,
  QUESTION_MARK: helpIcon,
};

// Recursively render menu items with either MenuLink or FlyoutLink,
// based on the hierarchical depth of the item.
const renderItems = (mq, items, MenuLink, hasScrolled) => {
  return items.map(({ id, icon, title, link, items = [] }) => {
    const alignIcon = mq.lte('md') || hasScrolled ? 'aside' : 'above';
    // Render sub menu
    if (items.length) {
      return mq.value === 'xs' ? (
        <MenuSlide mq={mq} title={title}>
          {renderItems(mq, items, MenuLink)}
        </MenuSlide>
      ) : (
        <MenuItem key={id}>
          <FlyoutMenu
            menuId={`menu-${id}`}
            align={mq.lte('md') ? 'right' : 'left'}
            closeOnMouseLeave={true}
          >
            <FlyoutToggle
              icon={iconMap[icon]}
              alignIcon={alignIcon}
              center={mq.lte('md') || hasScrolled}
              children={title}
            />
            {renderItems(mq, items, FlyoutLink)}
          </FlyoutMenu>
        </MenuItem>
      );
    }

    return isLogoutLink(link) ? (
      <MenuItem key={id}>
        <Mutation mutation={logoutMutation} onCompleted={onLogout}>
          {(logout, { loading, error }) => (
            <LinkManager to={link && (link.path || link.url)}>
              {({ isActive, linkProps }) => (
                <Link {...linkProps}>
                  <MenuLink
                    isActive={isActive}
                    center={mq.lte('md') || hasScrolled}
                    children={title}
                    onClick={e => {
                      e.preventDefault();
                      !loading && !error && logout();
                    }}
                  />
                </Link>
              )}
            </LinkManager>
          )}
        </Mutation>
      </MenuItem>
    ) : (
      <MenuItem key={id}>
        <LinkManager to={link && (link.path || link.url)}>
          {({ isActive, linkProps }) => (
            <Link {...linkProps}>
              <MenuLink
                isActive={isActive}
                icon={iconMap[icon]}
                alignIcon={alignIcon}
                center={mq.lte('md') || hasScrolled}
                children={title}
              />
            </Link>
          )}
        </LinkManager>
      </MenuItem>
    );
  });
};

const MenuItems = ({ mainMenu, userMenu, hasScrolled, ...props }) => {
  return (
    <Media>
      {mq => {
        // TODO: remove this once the login functionality is in place
        const userMenuWithoutLogin = userMenu.filter(
          item => item.title !== 'Log in'
        );

        const mainMenuItems = renderItems(
          mq,
          mainMenu,
          mq.value === 'xs' ? FlyoutLink : MenuLink,
          hasScrolled
        );

        const userMenuItems = renderItems(
          mq,
          userMenuWithoutLogin,
          mq.lte('md') ? FlyoutLink : MenuLink,
          hasScrolled
        );

        return mq.value === 'xs' ? (
          <OffCanvasDrawer>
            {mainMenuItems}
            <VerticalSpacing size={7} />
            {userMenuItems}
          </OffCanvasDrawer>
        ) : mq.value === 'sm' ? (
          <BurgerMenu
            menuId="menu-top"
            collapsed={mq.value === 'sm'}
            closeOnMouseLeave={false}
          >
            <MenuList>{mainMenuItems}</MenuList>

            <MenuList children={userMenuItems} />
          </BurgerMenu>
        ) : (
          <BurgerMenu
            menuId="menu-top"
            collapsed={mq.lte('sm')}
            closeOnMouseLeave={false}
          >
            <MenuList>{mainMenuItems}</MenuList>

            <BurgerMenu
              menuId="menu-user"
              collapsed={mq.gt('sm') && mq.lte('md')}
            >
              <MenuList children={userMenuItems} />
            </BurgerMenu>
          </BurgerMenu>
        );
      }}
    </Media>
  );
};

MenuItems.propTypes = {
  mainMenu: PropTypes.arrayOf(PropTypes.object),
  userMenu: PropTypes.arrayOf(PropTypes.object),
  hasScrolled: PropTypes.bool,
};

export default MenuItems;
